// components/models/HailuoFastControlPanel.tsx
import React from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import Select from '../ui/Select';
import { HailuoFastParams } from '#types/types';
import { useKernel } from '@/kernel/Kernel';

interface HailuoFastControlPanelProps {
  params: HailuoFastParams;
  setModelParams: (params: (prevParams: { [model: string]: HailuoFastParams }) => { [model: string]: HailuoFastParams }) => void;
  diffusionModel: string;
}

const defaultHailuoFastParams: HailuoFastParams = {
  prompt: '',
  duration: 6,
  seed: -1,
};

export const getDefaultHailuoFastParams = (): HailuoFastParams => ({ ...defaultHailuoFastParams });

// The endpoint takes a "6" | "10" enum, converted from this number in app/api/film/route.ts.
const DURATION_OPTIONS = [
  { value: '6', label: '6s' },
  { value: '10', label: '10s' },
];

const HailuoFastControlPanel: React.FC<HailuoFastControlPanelProps> = React.memo(({
  params, setModelParams, diffusionModel,
}) => {
  const { kernel } = useKernel();

  const handleParamChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const newValue = type === 'number' || type === 'range' ? parseFloat(value) : value;

    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[HailuoFastControlPanel] handleParamChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: {
          ...currentParams,
          [name]: newValue,
        },
      };
    });

    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      ...(name === 'prompt' ? { value_length: (newValue as string).length } : { value: newValue }),
      model_name: diffusionModel,
      source_component: 'HailuoFastControlPanel',
    });
  };

  const handleDurationChange = (value: string) => {
    const newValue = Number(value);
    setModelParams(prevModelParams => {
      const currentParams = prevModelParams[diffusionModel];
      if (!currentParams) {
        console.error(`[HailuoFastControlPanel] handleDurationChange: Could not find params for model "${diffusionModel}". Aborting state update.`);
        return prevModelParams;
      }
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, duration: newValue },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: 'duration',
      value: newValue,
      model_name: diffusionModel,
      source_component: 'HailuoFastControlPanel',
    });
  };

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe the motion you want from the current frame..."
          name="prompt"
          value={params.prompt}
          onChange={handleParamChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <Select
          id="hailuo-02-fast-duration"
          label="Duration"
          value={String(params.duration)}
          options={DURATION_OPTIONS}
          onChange={handleDurationChange}
        />
      </div>

      <p className="mb-4 text-xs text-[#777777]">
        Fixed 512p, animated from the current workspace frame.
      </p>
    </>
  );
});

HailuoFastControlPanel.displayName = 'HailuoFastControlPanel';

export default HailuoFastControlPanel;
